import type { TurnAbout } from './api';

/**
 * "Have thoughts about this?" (2026-09-24 拍板, §4.5 / §4.20 / §4.18): the door
 * at the foot of a weekly letter and of the person's own Ember page. It opens
 * the typed conversation with a first sentence already in the field and the
 * object riding along as what that turn is about — the server hands the talk
 * model the letter or the Ember in full on that one turn (edge about.js). The
 * sentence is the person's to change; the chip over the field says what
 * travels with it and lets them drop it.
 *
 * The page carries all of it on the query string, read once by Talk and
 * taken off the URL: `draft` (the sentence), `about` (`letter:<id>` /
 * `ember:<id>`), `about_label` (the chip's words).
 */
export function talkAboutHref(about: TurnAbout, draft: string, returnTo: string): string {
  const qs = new URLSearchParams({ mode: 'chat', draft, about: `${about.kind}:${about.id}`, about_label: about.label });
  if (returnTo) qs.set('returnTo', returnTo);
  return `/talk?${qs.toString()}`;
}

/** The URL's `about=<kind>:<id>` read back; anything else is no reference. */
export function parseAboutParam(value: string | null, label: string | null): TurnAbout | null {
  const m = /^(letter|ember):([A-Za-z0-9_-]{1,80})$/.exec(String(value || ''));
  if (!m) return null;
  return { kind: m[1] as TurnAbout['kind'], id: m[2], label: String(label || '').slice(0, 120) };
}

/** "September 23": the letter's day as the sentence names it. */
function monthDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : '';
}

/** The door on a weekly letter: the sentence and the chip for it. */
export function letterAbout(letter: { id: string; written_at: string | null }): { about: TurnAbout; draft: string } {
  const day = monthDay(letter.written_at);
  return {
    about: { kind: 'letter', id: letter.id, label: day ? `Your letter from ${day} goes with this` : 'Your letter goes with this' },
    draft: day ? `Ember, I have some thoughts about your letter from ${day}.` : 'Ember, I have some thoughts about your letter.',
  };
}

/** The door on the person's own Ember page: the sentence and the chip for it. */
export function emberAbout(ember: { id: string; title: string | null }): { about: TurnAbout; draft: string } {
  const title = (ember.title || '').trim();
  return {
    about: { kind: 'ember', id: ember.id, label: title ? `“${title}” goes with this` : 'This Ember goes with this' },
    draft: title ? `Ember, I have some thoughts about this Ember — “${title}”.` : 'Ember, I have some thoughts about this Ember.',
  };
}
